import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const Sidebar = ({ categories, popularPosts, onCategorySelect }) => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const socialLinks = [
    { name: 'Twitter', icon: 'Twitter', url: '#', color: 'text-blue-500' },
    { name: 'LinkedIn', icon: 'Linkedin', url: '#', color: 'text-blue-600' },
    { name: 'GitHub', icon: 'Github', url: '#', color: 'text-gray-800' },
    { name: 'YouTube', icon: 'Youtube', url: '#', color: 'text-red-600' }
  ];

  return (
    <div className="space-y-6">
      {/* Categories Widget */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Folder" size={20} className="mr-2 text-primary" />
          Categories
        </h3>
        <div className="space-y-2">
          {categories.slice(0, 8).map((category) => (
            <button
              key={category.id}
              onClick={() => onCategorySelect(category.id)}
              className="flex items-center justify-between w-full p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150 text-left"
            >
              <span className="text-sm text-text-secondary hover:text-text-primary">
                {category.name}
              </span>
              <span className="text-xs bg-secondary-200 text-secondary-700 px-2 py-1 rounded-full">
                {category.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Popular Posts Widget */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="TrendingUp" size={20} className="mr-2 text-accent" />
          Popular Posts
        </h3>
        <div className="space-y-4">
          {popularPosts.map((post, index) => (
            <div key={post.id} className="flex space-x-3 group ">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg overflow-hidden">
                  <Image
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-text-primary line-clamp-2 group-hover:text-primary transition-colors duration-150">
                  {post.title}
                </h4>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-text-secondary">
                    {formatDate(post.publishedAt)}
                  </span>
                  <span className="text-xs text-text-secondary">•</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Eye" size={12} className="text-text-secondary" />
                    <span className="text-xs text-text-secondary">
                      {post.views}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center">
                <span className="text-xs font-bold text-primary bg-primary-50 w-6 h-6 rounded-full flex items-center justify-center">
                  {index + 1}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter Signup Widget */}
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-2 flex items-center">
          <Icon name="Mail" size={20} className="mr-2 text-primary" />
          Stay Updated
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          Get the latest articles and insights delivered to your inbox weekly.
        </p>
        
        {isSubscribed ? (
          <div className="flex items-center space-x-2 p-3 bg-success-50 border border-success-200 rounded-md">
            <Icon name="CheckCircle" size={16} className="text-success-600" />
            <span className="text-sm text-success-700 font-medium">
              Successfully subscribed!
            </span>
          </div>
        ) : (
          <form onSubmit={handleNewsletterSubmit} className="space-y-3">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              required
            />
            <button
              type="submit"
              className="w-full btn-primary text-sm py-2"
            >
              Subscribe Now
            </button>
          </form>
        )}
        
        <p className="text-xs text-text-secondary mt-3">
          No spam, unsubscribe at any time.
        </p>
      </div>

      {/* Social Media Widget */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Share2" size={20} className="mr-2 text-accent" />
          Follow Us
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              className="flex items-center space-x-2 p-3 rounded-md border border-border hover:bg-secondary-50 transition-colors duration-150"
            >
              <Icon name={social.icon} size={16} className={social.color} />
              <span className="text-sm text-text-secondary">
                {social.name}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* RSS Feed Widget */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Rss" size={20} className="mr-2 text-accent" />
          RSS Feed
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          Subscribe to our RSS feed to get updates in your favorite reader.
        </p>
        <a
          href="#"
          className="inline-flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-600 transition-colors duration-150 text-sm"
        >
          <Icon name="Rss" size={16} />
          <span>Subscribe to RSS</span>
        </a>
      </div>

      {/* Archive Widget */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Archive" size={20} className="mr-2 text-secondary" />
          Archive
        </h3>
        <div className="space-y-2">
          {[
            { month: 'January 2024', count: 8 },
            { month: 'December 2023', count: 12 },
            { month: 'November 2023', count: 15 },
            { month: 'October 2023', count: 10 },
            { month: 'September 2023', count: 14 }
          ].map((archive) => (
            <button
              key={archive.month}
              className="flex items-center justify-between w-full p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150 text-left"
            >
              <span className="text-sm text-text-secondary hover:text-text-primary">
                {archive.month}
              </span>
              <span className="text-xs bg-secondary-200 text-secondary-700 px-2 py-1 rounded-full">
                {archive.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;